import { reportFrame, stampLines, type StampInput } from '@mt/shared';
import { api } from './api';

/**
 * Ảnh báo cáo dùng chung (tài xế, nhân viên; X-PWA §6, yêu cầu anh Tiến 14/10/2026): khung 16:9 (ảnh dọc 9:16), không cắt ảnh,
 * in dấu Ngày – Giờ – Vị trí dưới đáy. Giờ lấy theo máy chủ (giờ VN); không gọi được máy chủ thì dùng giờ điện thoại và ghi rõ.
 * Không có GPS thì in "Không có vị trí". Trả về cặp base64 JPG giống scaleImageFile để các API ảnh cũ nhận nguyên.
 */

export interface GeoFix {
  lat: number;
  lng: number;
  accuracyM: number | null;
}

/** Vị trí điện thoại (từ chối quyền hoặc chờ quá waitMs thì null). */
export function getPosition(waitMs = 3000): Promise<GeoFix | null> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), waitMs);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer);
        resolve({
          lat: Math.round(p.coords.latitude * 1e6) / 1e6,
          lng: Math.round(p.coords.longitude * 1e6) / 1e6,
          accuracyM: Number.isFinite(p.coords.accuracy) ? p.coords.accuracy : null,
        });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: waitMs, maximumAge: 60_000 },
    );
  });
}

/** Giờ máy chủ + địa chỉ gần đúng; lỗi / mất mạng / quá 3 giây → giờ điện thoại, không địa chỉ. */
async function stampContext(pos: GeoFix | null): Promise<Pick<StampInput, 'at' | 'clock' | 'address'>> {
  const local = { at: new Date(), clock: 'device' as const, address: null };
  try {
    const r = await Promise.race([
      api.get<{ now: string; address: string | null }>('/geo/photo-stamp', pos ? { lat: pos.lat, lng: pos.lng } : undefined),
      new Promise<null>((res) => setTimeout(() => res(null), 3000)),
    ]);
    if (!r) return local;
    const at = new Date(r.now);
    return Number.isNaN(at.getTime()) ? local : { at, clock: 'server', address: r.address };
  } catch {
    return local;
  }
}

async function toBase64(canvas: HTMLCanvasElement, quality: number) {
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', quality));
  if (!blob) throw new Error('Trình duyệt không xử lý được ảnh');
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Cắt bớt chữ cho vừa bề ngang (thêm "…"). */
function fit(ctx: CanvasRenderingContext2D, text: string, maxW: number) {
  if (ctx.measureText(text).width <= maxW) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxW) t = t.slice(0, -1);
  return `${t}…`;
}

/** sha256 (hex) của file gốc: server dùng để chặn gửi lại ảnh cũ, vì ảnh đã in dấu giờ luôn khác nhau. Trình duyệt không hỗ trợ → null. */
async function fileSha256(file: File): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null;
    const d = new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
    return Array.from(d, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export interface ReportPhoto {
  data: string;
  thumb: string;
  /** sha256 file gốc (chống dùng lại ảnh cũ) */
  sourceSha256: string | null;
  lat: number | null;
  lng: number | null;
  /** Giờ đã in lên ảnh (ISO) */
  stampedAt: string;
}

/**
 * Chuẩn bị 1 ảnh báo cáo: khung 16:9/9:16, dấu Ngày – Giờ – Vị trí, bản lớn ≤ 1600px + bản nhỏ ≤ 400px.
 * pos: truyền vào nếu màn đã lấy vị trí; bỏ trống thì tự lấy.
 */
export async function prepareReportPhoto(file: File, opts: { pos?: GeoFix | null; waitMs?: number } = {}): Promise<ReportPhoto> {
  if (!/^image\/(jpeg|png|webp|gif|bmp|heic|heif)$/.test(file.type)) throw new Error(`${file.name}: chỉ nhận file ảnh (JPG, PNG…)`);
  const shaP = fileSha256(file);
  const posP = opts.pos !== undefined ? Promise.resolve(opts.pos) : getPosition(opts.waitMs ?? 3000);
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(`${file.name}: không đọc được ảnh`);
  }
  try {
    const pos = await posP;
    const ctxInfo = await stampContext(pos);
    const f = reportFrame(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = f.width;
    canvas.height = f.height;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, f.width, f.height);
    ctx.drawImage(bitmap, f.dx, f.dy, f.dw, f.dh);

    const lines = stampLines({ ...ctxInfo, lat: pos?.lat ?? null, lng: pos?.lng ?? null, accuracyM: pos?.accuracyM ?? null });
    const short = Math.min(f.width, f.height);
    const fs = Math.max(12, Math.round(short * 0.032));
    const lineH = Math.round(fs * 1.3);
    const padX = Math.round(fs * 0.7);
    const bandH = lines.length * lineH + Math.round(fs * 0.8);
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, f.height - bandH, f.width, bandH);
    ctx.textBaseline = 'top';
    lines.forEach((text, i) => {
      ctx.font = `${i === 0 ? '600 ' : ''}${fs}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = i === 0 ? '#fff' : '#e5e7eb';
      ctx.fillText(fit(ctx, text, f.width - padX * 2), padX, f.height - bandH + Math.round(fs * 0.4) + i * lineH);
    });

    const ratio = 400 / Math.max(f.width, f.height);
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(f.width * ratio));
    small.height = Math.max(1, Math.round(f.height * ratio));
    small.getContext('2d')!.drawImage(canvas, 0, 0, small.width, small.height);

    const [data, thumb, sourceSha256] = await Promise.all([toBase64(canvas, 0.85), toBase64(small, 0.8), shaP]);
    return { data, thumb, sourceSha256, lat: pos?.lat ?? null, lng: pos?.lng ?? null, stampedAt: ctxInfo.at.toISOString() };
  } finally {
    bitmap.close();
  }
}
